import { Product, Source } from './product';
import { AbstractControl } from '@angular/forms';
import * as CodiceFiscale from 'codice-fiscale-js';
import * as IBAN from 'iban';
import { ageCf, getAge } from '../utils/cf';

export enum Sex {
  male = 'M',
  female = 'F',
}
export enum ShipMethod {
  email = 'Email',
  posta = 'Posta',
}

export enum BillingPeriodicity {
  monthly = 'Mensile',
  bimonthly = 'Bimestrale',
}

export enum GasUse {
  riscaldamento = 'Solo Riscaldamento C1',
  cottura = 'Cottura Cibi, Acqua Calda C2',
  acqua = 'Riscaldamento, Cottura Cibi, Acqua Calda C3',
}
export enum PaymentType {
  rid = 'Rid',
  bollettino = 'Bollettino',
  libretto = 'Libretto',
}
export enum LeadSource {
  drops = 'Web Onboarding Drops',
  complete = 'Web Onboarding',
  prospect = 'Web Onboarding Prospects',
  recall = 'Web',
  blacklist = 'Blacklist',
}

export enum Status {
  userInfo = 'user_info',
  contractInfo = 'contract_info',
  supplies = 'supplies',
  payment = 'payment',
  insurance = 'insurance',
  summary = 'summary',
  complete = 'complete',
  callme = 'callme',
  exitPopup = 'exit_popup',
}

export enum PuntoDiContatto {
  chetariffa = 'Chetariffa',
  newsletter = 'Newsletter',
  segugio = 'Segugio',
  altroconsumo = 'Altroconsumo',
  seisicuro = '6sicuro',
  lucegas = 'Luce-Gas',
  comparasemplice = 'ComparaSemplice',
  sicheconviene = 'Sicheconviene',
  compararisparmia = 'ComparaRisparmia',
  web = 'Web',
  facebook = 'Facebook',
  whatsapp = 'Whatsapp',
  sms = 'SMS',
  portaleofferte = 'Portaleofferte',
  exitpopup = 'Exit pop-up',
}

export const statusOrder = {
  user_info: 1,
  contract_info: 2,
  supplies: 3,
  payment: 4,
  insurance: 5,
  summary: 6,
  complete: 7,
};

export const getStepNumber = (status: Status | string): number => statusOrder[status];
export const getStepByNumber = (num: number): Status => {
  return Object.keys(statusOrder).find((step) => statusOrder[step] === num) as Status;
};

export enum Channel {
  default = '',
  tel = 'Canale Telefonico',
}

export enum OnboardingType {
  onboarding = 'Onboarding',
  callme = 'Callme',
}

export const onboardingEndpoint = 'onboarding';

export interface Onboarding {
  _id: string;
  feId: string;
  cf: string;
  product: any;
  firstname: string;
  typeId: OnboardingType;
  lastname: string;
  sex: Sex;
  birthDate: string;
  city: string;
  province: string;
  email: string;
  phone: number;
  prefix: number;

  privacyComCom: string;
  privacyProf: string;
  privacyQualServ: string;
  privacyComTer: string;
  privacyProfTer: string;
  generalPrivacy: boolean;

  communicationPrivacy: boolean;
  salePrivacy: boolean;
  billShipMethod: ShipMethod;
  billingPeriodicity: BillingPeriodicity;
  residenceAddress: string;
  residenceNumber: string;
  residenceCity: string;
  residenceProvince: string;
  residenceCap: number;
  residenceProvinceCode: string;
  residenceNation: string;
  residenceToponym: string;

  differentForniture: boolean;
  fornitureAddress: string;
  fornitureProvince: string;
  fornitureNumber: string;
  fornitureCity: string;
  fornitureCap: string;
  fornitureProvinceCode: string;
  fornitureNation: string;
  fornitureToponym: string;

  lightSupplier: string;
  lightConsumption: string;
  lightPower: number;
  lightPOD: string;
  lightBill: string;
  lightGreen: boolean;
  gasConsumption: number;
  gasPDR: string;
  gasUse: GasUse;
  gasBill: string;

  codeCard: string;
  paymentType: PaymentType;
  iban: string;
  differentIbanAccountholder: any;
  sottoscrittoreFullname: string;
  sottoscrittoreCF: string;
  librettoValidation: string;
  librettoCode: string;
  activationType: string;
  leadSource: LeadSource;
  copyResidence: boolean;
  webStatus: Status;
  channel: Channel;

  sendEmail: boolean;
  isPromoSelectedFriend: boolean;
  isPromoSelected: boolean;
  isFriendSelected: boolean;
  promoCode: string;
  isDefaultPromo: boolean;
  friendCode: string;
  call: string;
  isSocio: boolean;
  socioType: string;
  codeSocioCard: string;
  discountPromo: string;
  extraPoints: string;

  economicCondition: boolean;
  suppliesCondition: boolean;
  cadastralCondition: boolean;
  clauseCondition: boolean;
  consentsCondition: boolean;
  puntoDiContatto: string;

  otp_date: string;
  otp_phone: number;
  otp_verified: boolean;
  otp_sms_sent: number;
  otp_four_digit: string;
  manualInsertSupply: boolean;
  manualInsertAddress: boolean;
  manualInsertNameSupply: string;
  manualInsertNameAddress: string;
  manualInsertDateSupply: string;
  manualInsertDateAddress: string;
  Alg_GASInsurancePolicy__c: boolean;
  Alg_LIGHTInsurancePolicy__c: boolean;
  Alg_GASShowPolicy__c: boolean;
  Alg_LIGHTShowPolicy__c: boolean;
  informativeSetShowLight: boolean;
  informativeSetShowGas: boolean;
  linearDocumentFront: any;
  linearDocumentBack: any;
  contactAtHour: string;
  contactAtDay: string;
  vulnerabilityType?: string;
  cardValidationOutcome: null | string;
  cardValidationDescription: null | string;
  contractsMemberOutcome: null | string;
}

export const OnbValidators = {
  cf: (control: AbstractControl): { [key: string]: any } => {
    if (CodiceFiscale.check(control.value)) {
      return null;
    }
    return { cf: true };
  },
  cfOnb:
    (source: string, isFirst: boolean) =>
    (control: AbstractControl): { [key: string]: any } => {
      if (source === Source.accendiUnicaCoop && !isFirst) {
        return { cf: true };
      }
      return null;
    },
  sameCf:
    (insertedCf: string, coopCf: string) =>
    (control: AbstractControl): { [key: string]: any } => {
      if (insertedCf !== coopCf) {
        return { cf: true };
      }
      return null;
    },
  promo:
    (availableValue: any[], curdur, curOnb: string, productType: string) =>
    (control: AbstractControl): { [key: string]: any } => {
      if (control.value) {
        let duration;
        let promo;
        let type;
        let durationCheck = false;
        let ageCheck = false;
        let typeCheck = false;
        if (
          availableValue.find((e) => {
            duration = e.duration;
            type = e.productType;
            promo = e;
            return control.value.toUpperCase() === e.code;
          })
        ) {
          //TYPE CHECKE
          if (type && type.toLowerCase() !== 'tutte') {
            typeCheck = type === productType ? true : false;
          } else {
            typeCheck = true;
          }

          // DURATION CHECK
          if (duration === 'Tutte') {
            durationCheck = true;
          } else {
            if (duration === curdur) {
              durationCheck = true;
            } else {
              durationCheck = false;
            }
          }

          if (durationCheck) {
            // AGE CHECK
            if (promo.ageLimitEnabled) {
              let age = getAge(ageCf(curOnb));
              age = age < 18 ? age + 100 : age;
              ageCheck = eval(`${age} ${promo.ageLimitOperator} ${promo.ageLimit}`);
              if (ageCheck) {
                if (!typeCheck)
                  return {
                    promo: 'PROMO_CODE_TWO_INVALID',
                    promo2: promo.ageLimitErrorMessage,
                  };
                return null;
              } else {
                return {
                  promo: 'PROMO_INVALID',
                  promo2: promo.ageLimitErrorMessage,
                };
              }
            } else {
              if (!typeCheck)
                return {
                  promo: 'PROMO_CODE_TWO_INVALID',
                  promo2: promo.ageLimitErrorMessage,
                };
              return null;
            }
          } else {
            return { promo: 'PROMO_DURATION_INVALID' };
          }
        }
      }
      if (control && control.value && control.value.length === 0) return { promo: 'PROMO_CODE_REQUIRED' };
      return { promo: 'PROMO_CODE_ONE_INVALID' };
    },
  iban: (control: AbstractControl): { [key: string]: any } => {
    if (IBAN.isValid(control.value)) {
      return null;
    }
    return { iban: true };
  },
};



// WEBPACK FOOTER //
// ./src/app/models/onboarding.ts